// React hook owning the activity dock's persisted position + a transient
// drag preview. Pure math lives in `activityDockPositionMath.ts`.
//
// Phase 4 update (docs/planning/org_activity_floating_dock.md §12):
// the position model now supports all four viewport edges. The hook
// re-exports the math surface so existing callers keep working.

import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_POSITION,
  DOCK_POSITION_STORAGE_KEY,
  HEDGE_TAB_HEIGHT_PX,
  HEDGE_TAB_WIDTH_PX,
  SIDE_TAB_HEIGHT_PX,
  SIDE_TAB_WIDTH_PX,
  clampOffsetRatio,
  clampPositionToViewport,
  detectKeyboardOpen,
  isHorizontalEdge,
  parseStoredPosition,
  positionFromCoords,
  snapToNearestEdge,
  type ActivityDockPosition,
  type DockEdge,
} from "./activityDockPositionMath";
import { FIREFLY_DOCK_HIT_DIAMETER_PX } from "./firefly/fireflyDockGeometry";

export {
  DEFAULT_POSITION,
  DOCK_POSITION_STORAGE_KEY,
  HEDGE_TAB_HEIGHT_PX,
  HEDGE_TAB_WIDTH_PX,
  SIDE_TAB_HEIGHT_PX,
  SIDE_TAB_WIDTH_PX,
  clampOffsetRatio,
  detectKeyboardOpen,
  isHorizontalEdge,
  parseStoredPosition,
  positionFromCoords,
  snapToNearestEdge,
};
export type { ActivityDockPosition, DockEdge };

function readStoredPosition(): ActivityDockPosition {
  if (typeof window === "undefined") return DEFAULT_POSITION;
  return (
    parseStoredPosition(window.localStorage.getItem(DOCK_POSITION_STORAGE_KEY)) ??
    DEFAULT_POSITION
  );
}

function writeStoredPosition(pos: ActivityDockPosition): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DOCK_POSITION_STORAGE_KEY, JSON.stringify(pos));
  } catch {
    // Quota exceeded or storage disabled — non-fatal, fall through to memory.
  }
}

/**
 * Hook owning the dock's persisted position and a transient drag preview.
 * The component renders from `position` normally and from `dragPreview` while
 * `isDragging` is true. `tabWidthPx` / `tabHeightPx` are the circular
 * firefly hit target (square bounding box, same on every edge).
 */
export function useActivityDockPosition() {
  const [position, setPosition] = useState<ActivityDockPosition>(() => readStoredPosition());
  const [dragPreview, setDragPreview] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    function onResize() {
      setPosition((prev) => clampPositionToViewport(prev));
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const beginDrag = useCallback((x: number, y: number) => {
    setDragPreview({ x, y });
  }, []);

  const updateDrag = useCallback((x: number, y: number) => {
    setDragPreview({ x, y });
  }, []);

  const endDrag = useCallback(
    (x: number, y: number, viewportW: number, viewportH: number) => {
      const next = positionFromCoords({ x, y, viewportW, viewportH });
      setPosition(next);
      writeStoredPosition(next);
      setDragPreview(null);
    },
    [],
  );

  const cancelDrag = useCallback(() => {
    setDragPreview(null);
  }, []);

  const tabWidthPx = FIREFLY_DOCK_HIT_DIAMETER_PX;
  const tabHeightPx = FIREFLY_DOCK_HIT_DIAMETER_PX;

  return {
    position,
    dragPreview,
    isDragging: dragPreview !== null,
    beginDrag,
    updateDrag,
    endDrag,
    cancelDrag,
    /** Circular firefly hit target diameter (width and height). */
    tabWidthPx,
    tabHeightPx,
  };
}
